/*
 * UHS product card: Add to Cart
 * ------------------------------------------------------------
 * Document-level click handler for the `.js-uhs-add-to-cart` buttons rendered by
 * snippets/uhs-product-card.liquid (slider, flash-sale and deal cards). Delegated rather than
 * a custom element so it also covers the slides Slick clones.
 *
 * After a successful add it does what the PDP add (assets/product-form.js) does:
 * - sends the page's <cart-drawer> / <cart-notification> `getSectionsToRender()` ids with the
 *   add and hands the response to its `renderContents()`, which redraws the drawer (or
 *   notification) and #cart-icon-bubble and opens it;
 * - with neither element on the page (cart type "page"), goes to the cart page;
 * - publishes PUB_SUB_EVENTS.cartUpdate so Dawn's cart subscribers refresh (guarded, so the
 *   add still works if pubsub.js / constants.js are missing).
 *
 * Button labels and the status message come from data-label-* attributes on the card,
 * translated in Liquid. If a label is missing the button text is left unchanged.
 */
(() => {
  const SUCCESS_RESET_DELAY = 1500;
  const ERROR_RESET_DELAY = 2000;

  // Clear first so the same message twice in a row is announced again.
  function announce(card, message) {
    const status = card?.querySelector('[data-uhs-card-status]');
    if (!status || !message) return;
    status.textContent = '';
    setTimeout(() => {
      status.textContent = message;
    }, 100);
  }

  function setLabel(button, label) {
    if (label) button.textContent = label;
  }

  function resetButton(button, text, delay) {
    setTimeout(() => {
      button.textContent = text;
      button.disabled = false;
    }, delay);
  }

  function publishCartUpdate(variantId, cartData) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'uhs-product-card',
      productVariantId: variantId,
      cartData,
    });
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.js-uhs-add-to-cart');
    if (!button || button.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const card = button.closest('.uhs-product-card');
    const labels = card?.dataset || {};
    const originalText = button.textContent.trim();
    const root = window.Shopify?.routes?.root || '/';
    const cartUrl = window.routes?.cart_url || `${root}cart`;
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    button.disabled = true;
    setLabel(button, labels.labelAdding);

    const body = { id: Number(variantId), quantity: 1 };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cart.setActiveElement(button);
    }

    let data;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      data = await response.json();

      if (!response.ok || data.status) {
        // Shopify's description is already in the shopper's language (e.g. "sold out").
        const error = new Error(data.description || data.message || 'Unable to add product');
        error.description = data.description;
        throw error;
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      setLabel(button, labels.labelTryAgain);
      announce(card, error.description || window.cartStrings?.error);
      resetButton(button, originalText, ERROR_RESET_DELAY);
      return;
    }

    if (!cart) {
      window.location = cartUrl;
      return;
    }

    publishCartUpdate(variantId, data);

    try {
      cart.renderContents(data);
      cart.classList.remove('is-empty');
    } catch (error) {
      // The item is in the cart; only the redraw failed, so show the cart page instead.
      console.error('Cart refresh error:', error);
      window.location = cartUrl;
      return;
    }

    setLabel(button, labels.labelAdded);
    announce(card, labels.labelItemAdded);
    resetButton(button, originalText, SUCCESS_RESET_DELAY);
  });
})();
